import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  Scan,
  Plus,
  Search,
  Apple,
  Coffee,
  Sandwich,
  Pizza,
  Utensils,
  Trash2,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";


const meals = [
  { id: "breakfast", label: "Breakfast", icon: Coffee },
  { id: "lunch", label: "Lunch", icon: Sandwich },
  { id: "snacks", label: "Snacks", icon: Apple },
  { id: "dinner", label: "Dinner", icon: Pizza },
];

const FoodTracking = () => {
  const { user } = useAuth();
  const [selectedMeal, setSelectedMeal] = useState("breakfast");

  // Search & modal states
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // selected food + quantity modal
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantityGrams, setQuantityGrams] = useState(100); // default 100g
  const [adding, setAdding] = useState(false);

  // today's meals
  const [todayMeals, setTodayMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(false);

  // error / info
  const [error, setError] = useState("");

const navigate = useNavigate();


  useEffect(() => {
    if (user) fetchTodayMeals();
    // eslint-disable-next-line
  }, [user, selectedMeal]);

  // Search food_database by name (ilike)
  const searchFoods = async (text) => {
    setQuery(text);
    setError("");
    if (!text || text.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from("food_database")
        .select("id, name, calories, protein, carbs, fat, fiber, serving_size")
        .ilike("name", `%${text}%`)
        .limit(30);

      if (error) {
       
        setError("Search failed. Try again.");
        setSearchResults([]);
        return;
      }

      setSearchResults(data || []);
    } catch (err) {
      
      setError("Search failed. Try again.");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Open quantity modal for a chosen food
  const handleSelectFood = (food) => {
    setSelectedFood(food);
    // default quantity: 100 if calories are per 100g
    setQuantityGrams(100);
  };

  // Add food to meal_logs with nutrition scaled by grams/100
const addFoodToMeal = async () => {
  if (!user) {
    setError("User not found. Please sign in.");
    return;
  }

  if (!selectedFood) {
    setError("No food selected.");
    return;
  }

  const grams = Number(quantityGrams);

  if (!grams || grams <= 0) {
    setError("Quantity must be greater than 0.");
    return;
  }

  setAdding(true);
  setError("");

  try {
    const multiplier = grams / 100;

    const payload = {
      user_id: user.id,
      meal_type: selectedMeal,
      food_id: selectedFood.id,
      quantity: grams,
      calories: Number((selectedFood.calories || 0) * multiplier),
      protein: Number((selectedFood.protein || 0) * multiplier),
      carbs: Number((selectedFood.carbs || 0) * multiplier),
      fat: Number((selectedFood.fat || 0) * multiplier),
      created_at: new Date().toISOString(),
    };

   

    const { error } = await supabase.from("meal_logs").insert(payload);

    if (error) {
    
      setError(error.message || "Failed to add item.");
      return;
    }

    await fetchTodayMeals();
    setSelectedFood(null);
    setSearchOpen(false);
  } catch (err) {
  
    setError("Something went wrong.");
  } finally {
    setAdding(false);
  }
};


  // Fetch today's meals for the user (all meal types)
  const fetchTodayMeals = async () => {
    if (!user) return;
    setMealsLoading(true);
    setError("");
    try {
      const startOfDay = dayjs().startOf("day").toISOString();

      const { data, error } = await supabase
        .from("meal_logs")
        .select("*")
        .eq("user_id", user.id)
        .gte("created_at", startOfDay)
        .order("created_at", { ascending: true });

      if (error) {
       
        setError("Failed to load meals.");
        setTodayMeals([]);
        setMealsLoading(false);
        return;
      }

      setTodayMeals(data || []);
    } catch (err) {
     
      setError("Failed to load meals.");
      setTodayMeals([]);
    } finally {
      setMealsLoading(false);
    }
  };

  // Remove a meal_log item
const removeMealItem = async (id) => {
  const ok = window.confirm("Remove this item from your log?");
  if (!ok) return;

  try {
    const { error } = await supabase.from("meal_logs").delete().eq("id", id);

    if (error) {
    
      setError("Failed to remove item.");
      return;
    }

    await fetchTodayMeals();
  } catch (err) {
   
    setError("Failed to remove item.");
  }
};


  // compute totals per selectedMeal
  const mealItems = todayMeals.filter((m) => m.meal_type === selectedMeal);
  const totals = mealItems.reduce(
    (acc, it) => {
      acc.calories += Number(it.calories || 0);
      acc.protein += Number(it.protein || 0);
      acc.carbs += Number(it.carbs || 0);
      acc.fat += Number(it.fat || 0);
      return acc;
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Food Tracking</h2>
        <p className="text-gray-500 mt-1">Track your meals and nutrition</p>
      </div>

      {/* Meal Selector */}
<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
  {meals.map((meal) => {
    const Icon = meal.icon;
    const isActive = selectedMeal === meal.id;
    
    // Logic to calculate calories specifically for this meal tile
    const currentMealTotal = todayMeals
      .filter((m) => m.meal_type === meal.id)
      .reduce((acc, it) => acc + Number(it.calories || 0), 0);

    return (
      <motion.button
        key={meal.id}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
        onClick={() => setSelectedMeal(meal.id)}
        className={`relative p-4 rounded-[1.5rem] transition-all duration-300 text-left overflow-hidden border-2
          ${isActive 
            ? "border-emerald-500 bg-white shadow-lg shadow-emerald-100/50" 
            : "border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-200"
          }`}
      >
        {/* Visual highlight for active state */}
        {isActive && (
          <motion.div 
            layoutId="activeGlow"
            className="absolute top-0 left-0 w-1 h-full bg-emerald-500" 
          />
        )}

        <div className="flex flex-col gap-3">
          <div className={`w-10 h-8 rounded-xl flex items-center justify-center transition-colors
            ${isActive ? "bg-emerald-500 text-white" : "bg-white text-slate-400 shadow-sm"}
          `}>
            <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
          </div>

          <div>
            <p className={`font-black tracking-tight text-sm uppercase
              ${isActive ? "text-slate-900" : "text-slate-500"}
            `}>
              {meal.label}
            </p>
            
            <div className="mt-1">
              {currentMealTotal > 0 ? (
                <p className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full inline-block">
                  {Math.round(currentMealTotal)} kcal
                </p>
              ) : (
                <p className="text-[11px] font-bold text-slate-400">
                  Empty
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.button>
    );
  })}
</div>

      {/* Tools: AI scan / barcode / search */}
   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  <motion.button
    whileHover={{ y: -4, scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className="relative overflow-hidden bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-6 rounded-[2rem] flex items-center space-x-5 shadow-xl shadow-emerald-200/50 group"
    onClick={() => navigate("/scan")}
  >
    {/* Decorative background element */}
    <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-colors" />
    
    <div className="relative w-14 h-14 bg-white/20 backdrop-blur-md border border-white/30 rounded-2xl flex items-center justify-center shadow-inner">
      <Camera className="w-7 h-7 text-white" />
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center border-2 border-emerald-600">
        <span className="text-[8px] font-bold text-emerald-900">✨</span>
      </div>
    </div>

    <div className="text-left">
      <p className="font-bold text-xl tracking-tight">AI Photo Scan</p>
      <p className="text-emerald-100/80 text-sm font-medium">Log meals with one snap</p>
    </div>
  </motion.button>

  {/* Search Food Button */}
  <motion.button
    whileHover={{ y: -4, scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className="bg-white border border-slate-100 p-6 rounded-[2rem] flex items-center space-x-5 shadow-xl shadow-slate-200/50 hover:border-emerald-200 transition-all group"
    onClick={() => {
      setSearchOpen(true);
      setQuery("");
      setSearchResults([]);
    }}
  >
    <div className="w-14 h-14 bg-slate-50 group-hover:bg-emerald-50 rounded-2xl flex items-center justify-center transition-colors">
      <Search className="w-7 h-7 text-slate-400 group-hover:text-emerald-600 transition-colors" />
    </div>

    <div className="text-left">
      <p className="font-bold text-xl tracking-tight text-slate-900">Library Search</p>
      <p className="text-slate-400 text-sm font-medium">Over 500,000+ food items</p>
    </div>
  </motion.button>
</div>

      {/* Today's Meals listing <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{`Today's ${selectedMeal.charAt(0).toUpperCase() + selectedMeal.slice(1)}`}</h3>
          <div className="text-sm text-gray-500">
            <span className="font-medium text-emerald-600 mr-2">
              {Math.round(totals.calories)} kcal
            </span>
            <span className="mr-1">•</span>
            <span className="mr-2">P: {Math.round(totals.protein)}g</span>
            <span className="mr-1">•</span>
            <span> C: {Math.round(totals.carbs)}g</span>
          </div>
        </div>

        {mealsLoading ? (
          <p className="text-gray-500">Loading...</p>
        ) : mealItems.length === 0 ? (
          <div className="p-6 rounded-xl bg-gray-50 text-center text-gray-500">
            No items logged for {selectedMeal}. Click <strong>Add Food Item</strong> to add.
          </div>
        ) : (
          <div className="space-y-3">
            {mealItems.map((food) => (
              <div
                key={food.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{food.food_name}</p>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                    
                    <span className="text-gray-400"> </span>
                    <span>P: {Math.round(food.protein)}g</span>
                    <span>C: {Math.round(food.carbs)}g</span>
                    <span>F: {Math.round(food.fat)}g</span>
                  </div>
                </div>

                <div className="text-right flex items-center gap-3">
                  <div>
                    <p className="font-semibold text-emerald-600">{Math.round(food.calories)}</p>
                    <p className="text-xs text-gray-400">kcal</p>
                  </div>
                  <button
                    onClick={() => removeMealItem(food.id)}
                    className="p-2 rounded-md hover:bg-red-50"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            setSearchOpen(true);
            setQuery("");
            setSearchResults([]);
          }}
          className="w-full mt-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-emerald-500 hover:text-emerald-600 transition-colors flex items-center justify-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Add Food Item</span>
        </motion.button>
      </div>*/}
      <div className="bg-white rounded-[2rem] p-8 shadow-xl shadow-slate-200/50 border border-slate-50">
  {/* Header Section */}
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
    <div>
      <h3 className="text-2xl font-black text-slate-900 tracking-tight leading-none">
        {selectedMeal.charAt(0).toUpperCase() + selectedMeal.slice(1)}
      </h3>
      <p className="text-slate-400 text-sm font-bold mt-2 uppercase tracking-widest">Today's Entry</p>
    </div>

    <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-2xl border border-slate-100">
      <div className="px-3 py-1 bg-white rounded-xl shadow-sm border border-slate-100">
        <span className="text-xs font-black text-emerald-600 mr-1">{Math.round(totals.calories)}</span>
        <span className="text-[10px] font-bold text-slate-400 uppercase">kcal</span>
      </div>
      <div className="hidden sm:flex gap-3 px-2">
        <div className="text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">P</p>
          <p className="text-xs font-bold text-slate-700">{Math.round(totals.protein)}g</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">C</p>
          <p className="text-xs font-bold text-slate-700">{Math.round(totals.carbs)}g</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">F</p>
          <p className="text-xs font-bold text-slate-700">{Math.round(totals.fat)}g</p>
        </div>
      </div>
    </div>
  </div>

  {/* Content Section */}
  {mealsLoading ? (
    <div className="py-12 flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-4 border-emerald-100 border-t-emerald-500 rounded-full animate-spin" />
      <p className="text-slate-400 font-bold text-sm tracking-widest uppercase">Fetching Log...</p>
    </div>
  ) : mealItems.length === 0 ? (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      className="py-12 px-6 rounded-[1.5rem] bg-slate-50/50 border-2 border-dashed border-slate-100 text-center"
    >
      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
        <Utensils className="text-slate-300 w-6 h-6" />
      </div>
      <p className="text-slate-500 font-bold">Your {selectedMeal} log is empty</p>
      <p className="text-slate-400 text-xs mt-1">Add items to track your progress</p>
    </motion.div>
  ) : (
    <div className="space-y-3">
      <AnimatePresence mode="popLayout">
        {mealItems.map((food) => (
          <motion.div
            key={food.id}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="group flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-50/50 transition-all duration-300"
          >
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 bg-slate-50 rounded-xl flex flex-col items-center justify-center border border-slate-100 group-hover:bg-emerald-50 group-hover:border-emerald-100 transition-colors">
                 <p className="text-[10px] font-black text-slate-400 uppercase leading-none mb-1">qty</p>
                 <p className="font-black text-slate-700 text-xs">{food.quantity}g</p>
              </div>
              
              <div className="flex-1">
                <p className="font-black text-slate-800 tracking-tight">{food.food_name}</p>
                <div className="flex items-center gap-3 mt-1">
                   <span className="text-[10px] font-bold text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded">P: {Math.round(food.protein)}g</span>
                   <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">C: {Math.round(food.carbs)}g</span>
                   <span className="text-[10px] font-bold text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded">F: {Math.round(food.fat)}g</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-black text-lg text-slate-900 leading-none">{Math.round(food.calories)}</p>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">kcal</p>
              </div>
              <button
                onClick={() => removeMealItem(food.id)}
                className="p-2.5 rounded-xl text-slate-300 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all duration-200"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )}

  {/* Footer Action */}
  <motion.button
    whileHover={{ scale: 1.01, backgroundColor: '#10b981', color: '#ffffff' }}
    whileTap={{ scale: 0.98 }}
    onClick={() => {
      setSearchOpen(true);
      setQuery("");
      setSearchResults([]);
    }}
    className="w-full mt-6 py-4 bg-emerald-50 text-emerald-600 rounded-2xl font-black text-sm tracking-widest uppercase transition-all flex items-center justify-center space-x-2 shadow-sm border border-emerald-100"
  >
    <Plus className="w-5 h-5 stroke-[3px]" />
    <span>Add to {selectedMeal}</span>
  </motion.button>
</div>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <input
                value={query}
                onChange={(e) => searchFoods(e.target.value)}
                placeholder="Search food by name (e.g. paneer, rice)..."
                className="flex-1 border p-3 rounded-xl focus:ring-2 focus:ring-emerald-500"
              />
              <button
                onClick={() => {
                  setSearchOpen(false);
                  setSelectedFood(null);
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                Close
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto space-y-2">
              {searchLoading && <p className="text-gray-500">Searching...</p>}
              {error && <p className="text-red-600">{error}</p>}
              {!searchLoading && searchResults.length === 0 && query.length >= 2 && (
                <p className="text-gray-500">No results found.</p>
              )}

              {searchResults.map((item) => (
                <div
                  key={item.id}
                  className="p-4 rounded-xl border cursor-pointer hover:bg-emerald-50 flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      {Math.round(item.calories || 0)} kcal • {item.serving_size || "per 100g"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleSelectFood(item)}
                      className="px-3 py-2 rounded-lg bg-emerald-600 text-white"
                    >
                      Add
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Quantity chooser (shown when selectedFood) */}
            {selectedFood && (
              <div className="mt-4 border-t pt-4 space-y-3">
                <h4 className="font-semibold">{selectedFood.name}</h4>
                <p className="text-sm text-gray-600">
                  Base: {selectedFood.calories} kcal / 100g (if values are per 100g)
                </p>

                <div className="flex gap-3 items-center">
                  <label className="text-sm text-gray-700">Quantity (grams)</label>
                  <input
                    type="number"
                    min={1}
                    value={quantityGrams}
                    onChange={(e) => setQuantityGrams(e.target.value)}
                    className="border p-2 rounded-lg w-32"
                  />

                  <div className="text-sm text-gray-600">
                    Estimated:{" "}
                    <strong>
                      {Math.round((selectedFood.calories || 0) * (quantityGrams / 100) || 0)} kcal
                    </strong>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedFood(null)}
                    className="px-4 py-2 rounded-lg border"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addFoodToMeal}
                    disabled={adding}
                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white disabled:opacity-60"
                  >
                    {adding ? "Adding..." : `Add to ${selectedMeal}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FoodTracking;



